/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React from 'react';

import { Colors, TypographyPDL } from '@zapier/design-system';

const checkboxStyles = css`
  .label {
    padding-left: var(--zds-space-2, 2px);
    ${TypographyPDL('--zds-body-1', 'paragraph3Medium')}
  }

  display: flex;
  align-items: center;
  gap: var(--zds-space-4, 4px);
  color: var(--zds-gray-warm-10, ${Colors.neutral800});
  forced-color-adjust: none;
  padding-left: var(--zds-space-2, 2px);

  .checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid var(--zds-gray-warm-6, ${Colors.neutral600});
    border-radius: var(--zds-radius-none, 3px);
    transition: all 40ms;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 14px;
    height: 14px;
    fill: none;
    stroke: var(--zds-prime-white, ${Colors.neutral100});
    stroke-width: 3px;
    stroke-dasharray: 22px;
    stroke-dashoffset: 66;
    transition: all 120ms;
  }

  &[data-has-no-visible-label] {
    padding: 0;
  }

  &[data-pressed] .checkbox {
    border-color: var(--zds-ui-primary-stronger, ${Colors.night});
  }

  &[data-focus-visible] {
    outline: var(--zds-border-focus, 2px solid ${Colors.blue});
    outline-offset: var(--zds-size-2, 3px);
  }

  &[data-selected],
  &[data-indeterminate] {
    .checkbox {
      border-color: var(--zds-ui-primary-stronger, ${Colors.blue600});
      background: var(--zds-ui-primary-stronger, ${Colors.blue600});
    }

    &[data-pressed] .checkbox {
      border-color: var(--zds-ui-primary-strongest, ${Colors.night});
      background: var(--zds-ui-primary-strongest, ${Colors.night});
    }

    .label {
      ${TypographyPDL('--zds-body-3', 'paragraph3Medium')}
    }

    svg {
      stroke-dashoffset: 44;
    }
  }

  &[data-invalid] {
    color: var(--zds-status-error-stronger, ${Colors.error});

    .checkbox {
      border-color: var(--zds-status-error-stronger, ${Colors.error});
    }

    &[data-selected] .checkbox {
      background: var(--zds-status-error-stronger, ${Colors.error});
    }

    &[data-pressed][data-selected] .checkbox {
      background: var(--zds-status-error-strongest, ${Colors.error});
      border-color: var(--zds-status-error-strongest, ${Colors.error});
    }

    &[data-pressed]:not([data-selected]) .checkbox {
      border-color: var(--zds-status-error-strongest, ${Colors.error});
    }

    &[data-selected] svg {
      stroke-dashoffset: 44;
    }
  }

  &[data-disabled] {
    color: var(--zds-text-weaker, ${Colors.neutral500});
    cursor: not-allowed;
    background: var(--zds-background-stronger, transparent);

    .checkbox {
      background: var(--zds-background-stronger, ${Colors.neutral300});
    }

    &:not([data-indeterminate='true']) svg {
      width: 12px;
      height: 12px;
      stroke: var(--zds-stroke-default, ${Colors.neutral500}) !important;
    }

    .checkbox {
      border-color: var(--zds-stroke-default, ${Colors.neutral500});
    }

    &[data-has-no-visible-label] {
      background: none;

      .checkbox {
        background: var(--zds-background-stronger, ${Colors.neutral300});
      }
    }
  }

  &[data-indeterminate] {
    &[data-invalid] .checkbox {
      background: var(--zds-status-error-stronger, ${Colors.error});
    }

    &[data-disabled] .checkbox {
      background: var(--zds-gray-warm-5, ${Colors.neutral500});
    }

    & svg {
      transition: none;
      stroke: none;
      top: 0px;
      fill: var(--zds-prime-white, ${Colors.neutral100});
    }
  }
`;

import type { CheckboxProps as AriaCheckboxProps } from 'react-aria-components';
import { Checkbox as AriaCheckbox } from 'react-aria-components';

export type CheckboxProps = {
  children?: React.ReactNode;
  /** The value of the input element, used when submitting an HTML form. See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefvalue | MDN}. */
  value?: string;
  /** The name of the input element, used when submitting an HTML form. See {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefname | MDN}. */
  name?: string;

  // ----- VALIDATION

  /**
   * Whether to use native HTML form validation to prevent form submission when the value is missing or invalid, or mark the field as required or invalid via ARIA.
   * @default 'native'
   */
  validationBehavior?: 'native' | 'aria';
  /**
   * A function that returns an error message if a given value is invalid. Validation errors are displayed to the user when the form is submitted if validationBehavior="native". For realtime validation, use the isInvalid prop instead.
   */
  validate?: AriaCheckboxProps['validate'];

  /** Whether the checkbox is checked on initial render (uncontrolled). */
  defaultSelected?: boolean;
  /** Whether the checkbox is checked (controlled). */
  isSelected?: boolean;

  /** Whether user input is required on the input before form submission. */
  isRequired?: boolean;
  /** Whether the input is disabled. */
  isDisabled?: boolean;
  /** Whether the input can be selected but not changed by the user. */
  isReadOnly?: boolean;
  /** Whether the input value is invalid. */
  isInvalid?: boolean;
  /** Controlled only.  Typically used where a "select all" checkbox would be if only some of the summarized checkboxes are checked. */
  isIndeterminate?: boolean;

  /** Whether the element should receive focus on render. */
  autoFocus?: boolean;

  // ------ STYLE OVERRIDES

  /** The CSS className for the element. A function may be provided to compute the class based on component state. */
  className?: AriaCheckboxProps['className'];
  /** The inline style for the element. A function may be provided to compute the style based on component state. */
  style?: AriaCheckboxProps['style'];

  // ------ EVENTS

  /** Handler that is called when the checkbox state changes. */
  onChange?: AriaCheckboxProps['onChange'];
  /** Handler that is called when focus is received. */
  onFocus?: AriaCheckboxProps['onFocus'];
  /** Handler that is called when focus is lost. */
  onBlur?: AriaCheckboxProps['onBlur'];
  /** Handler that is called when the focus status changes. */
  onFocusChange?: AriaCheckboxProps['onFocusChange'];
  /** Handler that is called when a key is pressed. */
  onKeyDown?: AriaCheckboxProps['onKeyDown'];
  /** Handler that is called when a key is released. */
  onKeyUp?: AriaCheckboxProps['onKeyUp'];
  /** Handler that is called when a hover interaction starts. */
  onHoverStart?: AriaCheckboxProps['onHoverStart'];
  /** Handler that is called when a hover interaction ends. */
  onHoverEnd?: AriaCheckboxProps['onHoverEnd'];
  /** Handler that is called when the hover state changes. */
  onHoverChange?: AriaCheckboxProps['onHoverChange'];

  // a11y
  id?: string;
  'aria-label'?: string;
  'aria-controls'?: AriaCheckboxProps['aria-controls'];
  'aria-labeledby'?: AriaCheckboxProps['aria-labelledby'];
  'aria-describedby'?: AriaCheckboxProps['aria-describedby'];
  'aria-details'?: AriaCheckboxProps['aria-details'];
  'aria-errormessage'?: AriaCheckboxProps['aria-errormessage'];
  excludeFromTabOrder?: AriaCheckboxProps['excludeFromTabOrder'];
};

/**
 * A checkbox allows a user to select multiple items from a list of individual items, or to mark one individual item as selected.
 *
 * Note: If there are a group of multiple checkboxes, use `CheckboxGroup`.
 *
 * ```ts
 * <Checkbox defaultSelected={true}>label</Checkbox>
 * ```
 */
export const Checkbox = ({
  children,
  validationBehavior = 'native',
  ...props
}: CheckboxProps) => {
  return (
    <AriaCheckbox
      css={checkboxStyles}
      data-zds
      validationBehavior={validationBehavior}
      data-has-no-visible-label={
        React.Children.count(children) === 0 ? true : undefined
      }
      {...props} // eslint-disable-line react/jsx-props-no-spreading
    >
      {({ isIndeterminate, isSelected }) => (
        <React.Fragment>
          <div className="checkbox" data-zds>
            <svg viewBox="0 0 18 18" aria-hidden="true" data-zds>
              {isIndeterminate ? (
                <rect x={0} y={7} width={18} height={4} />
              ) : (
                isSelected && <polyline points="2 9 8 14 16 4" />
              )}
            </svg>
          </div>
          <div className="label">{children}</div>
        </React.Fragment>
      )}
    </AriaCheckbox>
  );
};
